/**
 * Broad Path Matcher
 *
 * Takes a student's traitProfile (Record<traitId, 0-100>) and the broad path nodes,
 * and returns a ranked list of broad paths with match percentages.
 *
 * Algorithm (cosine-style weighted dot product):
 *
 *   For each broad node N:
 *     rawScore  = Σ  (studentScore[trait] / 100) * traitWeight
 *     maxScore  = Σ  traitWeight                         (if student scored 100 on all)
 *     matchPct  = round((rawScore / maxScore) * 100)
 *
 * This ensures a student who has ZERO of a required trait gets penalised proportionally,
 * while a perfect scorer on all required traits gets 100%.
 */

import type { CareerPathNode } from '../../Career Suggestion Engine/types';
import { TraitProfile } from './traitScoring';

export interface BroadPathMatch {
  node: CareerPathNode;
  matchPercentage: number;  // 0–100
  topMatchingTraits: Array<{ traitId: string; contribution: number }>; // sorted desc
}

/**
 * Score a single broad-path node against a student's trait profile.
 */
function scoreNode(node: CareerPathNode, profile: TraitProfile): BroadPathMatch {
  let rawScore = 0;
  let maxScore = 0;
  const traitContributions: Array<{ traitId: string; contribution: number }> = [];

  for (const trait of node.traits) {
    const studentScore = profile[trait.traitId] ?? 0;
    const weight = trait.weight ?? 5;
    const contribution = (studentScore / 100) * weight;
    rawScore += contribution;
    maxScore += weight;
    traitContributions.push({ traitId: trait.traitId, contribution });
  }

  const matchPercentage = maxScore > 0 ? Math.round((rawScore / maxScore) * 100) : 0;

  // Surface the traits that contributed most to this match
  const topMatchingTraits = traitContributions
    .sort((a, b) => b.contribution - a.contribution)
    .slice(0, 4);

  return { node, matchPercentage, topMatchingTraits };
}

/**
 * Match a student's trait profile against all broad path nodes.
 * Returns results sorted by matchPercentage descending.
 */
export function matchBroadPaths(
  profile: TraitProfile,
  nodes: CareerPathNode[]
): BroadPathMatch[] {
  return nodes
    .map((node) => scoreNode(node, profile))
    .sort((a, b) => b.matchPercentage - a.matchPercentage);
}

/**
 * Return the top N broad paths, with a natural cut-off:
 * only paths with matchPercentage >= minThreshold are included.
 */
export function getTopBroadPaths(
  profile: TraitProfile,
  nodes: CareerPathNode[],
  n = 4,
  minThreshold = 40
): BroadPathMatch[] {
  return matchBroadPaths(profile, nodes)
    .filter((m) => m.matchPercentage >= minThreshold)
    .slice(0, n);
}
